import LoadingButton from "@mui/lab/LoadingButton"
import { MenuItem, Tooltip, Button } from "@mui/material"
import { Clock, Armchair } from "lucide-mui"
import React from "react"

import { TextField, DateField } from "@/components/form"
import i18n from "@/i18n"
import { formatTableName } from "@/shared/tables"

import PublicPeopleAmountInput from "../../public_people_amount_input"
import { STYLES as SHARED_STYLES } from "../_styles"
import Footer from "../footer"
import Header from "../header"
import ReservationDetailsWithPlan from "../reservation_details_with_plan"
import ReservationDetailsWithoutMap from "../reservation_details_without_map"

import { OnboardingTooltip } from "./onboarding_tooltips"
import { type ReservationDetailsStepProps, useReservationDetailsState } from "./shared"
import { useOnboardingTooltips } from "./use_onboarding_tooltips"

const STYLES = {
  ...SHARED_STYLES,
  planLayoutContainer: "flex flex-col md:flex-row flex-1 min-h-0",
  planColumn: "flex-1 min-w-0 flex flex-col",
  tablesAvailableText: "text-[10px] text-[#5f6774] mt-1 block",
  timeFieldsRow: "flex items-center gap-2",
  timeSeparator: "text-[#9CA3AF] mt-4",
  durationText: "text-center text-xs text-[#5f6774]",
  actionsContainer: "mt-auto pt-3 flex items-center gap-2",
  noPlanContainer: "px-6 py-8",
} as const

const ReservationDetailsStepDesktop: React.FC<ReservationDetailsStepProps> = ({
  tables,
  openingHours,
  timezone,
  country,
  venueSlug,
  venueName,
  venueLogoUrl,
  venuePlans,
  address,
  city,
  minAdvanceBookingTime,
  onBack,
  onNext,
  onError,
  editMode = false,
  originalReservation,
}) => {
  const {
    values,
    setFieldValue: _setFieldValue,
    isChecking,
    setHasConflicts,
    getTableCapacityStatus,
    timeOptions,
    scheduleTimeOptions,
    closedUntil,
    endTimeOptions,
    isValid,
    handleNext,
    availableTablesCount,
    durationDisplay,
    hasPlanLayout,
  } = useReservationDetailsState({
    tables,
    openingHours,
    timezone,
    venueSlug,
    minAdvanceBookingTime,
    venuePlans,
    editMode,
    originalReservation,
    onNext,
    onError,
  })

  const { tooltipsVisible, dismissTooltips } = useOnboardingTooltips()

  const editSidebarFields = editMode ? (
    <>
      <div className="bg-[#f5f5f5] rounded-lg p-3 mb-2" data-testid="public-reservation-modal-edit-date-box">
        <span className={STYLES.sidebarSubtext}>{i18n.t("app.public.venue.modal.date")}</span>
        <DateField
          name="date"
          timezone={timezone}
          country={country}
          slotProps={{
            textField: {
              size: "x-small",
              fullWidth: true,
            },
          }}
        />
      </div>
      <div className="bg-[#f5f5f5] rounded-lg p-3 mb-2" data-testid="public-reservation-modal-edit-guests-box">
        <span className={STYLES.sidebarSubtext}>{i18n.t("app.public.venue.modal.guests")}</span>
        <PublicPeopleAmountInput name="peopleAmount" size="x-small" displayIcon />
      </div>
    </>
  ) : null

  const editNoPlanFields = editMode ? (
    <div className="flex gap-3 mb-4" data-testid="public-reservation-modal-edit-boxes-no-plan">
      <div className="bg-[#f5f5f5] rounded-lg p-3 flex-1">
        <span className="text-[10px] font-medium text-[#5f6774] block mb-1">
          {i18n.t("app.public.venue.modal.date")}
        </span>
        <DateField
          name="date"
          timezone={timezone}
          country={country}
          slotProps={{
            textField: {
              size: "x-small",
              fullWidth: true,
            },
          }}
        />
      </div>
      <div className="bg-[#f5f5f5] rounded-lg p-3 flex-1">
        <span className="text-[10px] font-medium text-[#5f6774] block mb-1">
          {i18n.t("app.public.venue.modal.guests")}
        </span>
        <PublicPeopleAmountInput name="peopleAmount" size="x-small" displayIcon />
      </div>
    </div>
  ) : null

  return (
    <>
      <Header />

      {hasPlanLayout ? (
        <div
          className={STYLES.planLayoutContainer}
          onClickCapture={dismissTooltips}
          onTouchStartCapture={dismissTooltips}
        >
          <div className={STYLES.planColumn}>
            <OnboardingTooltip
              open={tooltipsVisible}
              title={i18n.t("app.public.venue.modal.onboarding.click_table")}
              containerClassName="flex flex-1 min-h-0 min-w-0"
              placement="inside-top"
              positionClassName="top-12"
              data-testid="public-reservation-modal-details-onboarding-tooltip-map"
            >
              <div className="flex flex-1 min-h-0 min-w-0">
                <ReservationDetailsWithPlan
                  tables={tables}
                  venuePlans={venuePlans}
                  timeOptions={scheduleTimeOptions}
                  venueSlug={venueSlug}
                  closedUntil={closedUntil}
                  onConflictChange={setHasConflicts}
                  excludeReservationId={originalReservation?.id}
                  scheduleOnboardingTooltip={{
                    open: tooltipsVisible,
                    title: i18n.t("app.public.venue.modal.onboarding.select_time_range"),
                    "data-testid": "public-reservation-modal-details-onboarding-tooltip-schedule",
                  }}
                />
              </div>
            </OnboardingTooltip>
          </div>

          <div className={STYLES.sidebar} data-testid="public-reservation-modal-details-sidebar">
            <p className={STYLES.sidebarHeading}>
              {editMode
                ? i18n.t("app.public.venue.modal.modify_explore_floor")
                : i18n.t("app.public.venue.modal.explore_floor")}
            </p>

            {editSidebarFields}

            <div className={STYLES.sidebarFormGroup} data-testid="public-reservation-modal-details-form-table-time">
              <div>
                <span className={STYLES.sidebarSubtext}>{i18n.t("app.public.venue.modal.select_table")}</span>
                <TextField
                  select
                  name="tableId"
                  placeholder={i18n.t("app.public.venue.modal.select_table")}
                  size="x-small"
                  fullWidth
                  data-testid="public-reservation-modal-details-table-select"
                  InputProps={{
                    startAdornment: <Armchair sx={{ fontSize: 14 }} className="mr-1.5 text-[#5f6774]" />,
                  }}
                  InputLabelProps={{ shrink: false }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "30px",
                      fontSize: "12px",
                      borderRadius: "4px",
                    },
                  }}
                >
                  {tables.map((table) => {
                    const status = getTableCapacityStatus(table)
                    const menuItem = (
                      <MenuItem key={table.id} value={table.id} disabled={status.disabled}>
                        {formatTableName(table)}
                      </MenuItem>
                    )

                    if (status.disabled && status.tooltip) {
                      return (
                        <Tooltip key={table.id} title={status.tooltip} enterTouchDelay={0} placement="left">
                          <span>{menuItem}</span>
                        </Tooltip>
                      )
                    }
                    return menuItem
                  })}
                </TextField>
                <span
                  className={STYLES.tablesAvailableText}
                  data-testid="public-reservation-modal-details-tables-available"
                >
                  {i18n.t("app.public.venue.modal.tables_available", { count: availableTablesCount })}
                </span>
              </div>
            </div>
            <div className={STYLES.sidebarFormGroup} data-testid="public-reservation-modal-details-form-table-time">
              <div className={STYLES.timeFieldsRow}>
                <div className="flex-1">
                  <span className={STYLES.sidebarSubtext}>{i18n.t("app.public.venue.modal.start_time")}</span>
                  <TextField
                    select
                    name="startTime"
                    placeholder={i18n.t("app.public.venue.modal.start_time")}
                    disabled={!values.date}
                    size="x-small"
                    fullWidth
                    data-testid="public-reservation-modal-details-start-time-select"
                    InputProps={{
                      startAdornment: <Clock sx={{ fontSize: 14 }} className="mr-1.5 text-[#ACAEBA]" />,
                    }}
                    InputLabelProps={{ shrink: false }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "30px",
                        fontSize: "12px",
                        borderRadius: "4px",
                      },
                    }}
                  >
                    {timeOptions.map(([time, dt]) => (
                      <MenuItem key={time} value={dt.toISO()}>
                        {time}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>
                <span className={STYLES.timeSeparator}>-</span>
                <div className="flex-1">
                  <span className={STYLES.sidebarSubtext}>{i18n.t("app.public.venue.modal.end_time")}</span>
                  <TextField
                    select
                    name="endTime"
                    placeholder={i18n.t("app.public.venue.modal.end_time")}
                    disabled={!values.startTime}
                    size="x-small"
                    fullWidth
                    data-testid="public-reservation-modal-details-end-time-select"
                    InputLabelProps={{ shrink: false }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "30px",
                        fontSize: "12px",
                        borderRadius: "4px",
                      },
                    }}
                  >
                    {endTimeOptions.map(([time, dt]) => (
                      <MenuItem key={time} value={dt.toISO()}>
                        {time}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>
              </div>
              {durationDisplay ? (
                <div className={STYLES.durationText} data-testid="public-reservation-modal-details-duration">
                  {durationDisplay}
                </div>
              ) : null}
            </div>

            <div className={STYLES.actionsContainer} data-testid="public-reservation-modal-details-actions">
              <Button
                variant="outlined"
                size="small"
                onClick={onBack}
                disabled={isChecking}
                data-testid="public-reservation-modal-details-back-btn"
                sx={{
                  borderColor: "#e5e7eb",
                  color: "#5f6774",
                  borderRadius: "5px",
                  textTransform: "none",
                  fontSize: "12px",
                  "&:hover": { borderColor: "#2596fa", color: "#2596fa" },
                }}
              >
                {i18n.t("app.public.venue.modal.back")}
              </Button>
              <LoadingButton
                variant="contained"
                size="small"
                className={`flex-1 ${STYLES.sidebarNextBtn}`}
                onClick={handleNext}
                disabled={!isValid}
                loading={isChecking}
                data-testid="public-reservation-modal-details-next-btn"
                sx={{
                  backgroundColor: "#2596fa",
                  borderRadius: "5px",
                  textTransform: "none",
                  fontSize: "12px",
                  "&:hover": { backgroundColor: "#1e7cd8" },
                  "&.Mui-disabled": { backgroundColor: "#2596fa", opacity: 0.5 },
                }}
              >
                {i18n.t("app.public.venue.modal.next")}
              </LoadingButton>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className={STYLES.noPlanContainer}>
            {editNoPlanFields}
            <ReservationDetailsWithoutMap
              tables={tables}
              venueName={venueName}
              venueLogoUrl={venueLogoUrl}
              address={address}
              city={city}
            />
          </div>

          <Footer canGoNext={isValid} loading={isChecking} onBack={onBack} onNext={handleNext}>
            {editMode ? null : (
              <>
                <DateField
                  name="date"
                  timezone={timezone}
                  country={country}
                  slotProps={{
                    textField: {
                      size: "x-small",
                      className: STYLES.footerInput,
                    },
                  }}
                />
                <PublicPeopleAmountInput
                  name="peopleAmount"
                  size="x-small"
                  className={STYLES.footerInput}
                  displayIcon
                />
              </>
            )}
          </Footer>
        </>
      )}
    </>
  )
}

export default ReservationDetailsStepDesktop
